import { useNavigate } from 'react-router-dom';
import { Phone, Globe, Zap, BarChart3, ArrowRight, Headphones } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const features = [
  {
    icon: Phone,
    title: '24/7 Live Calls',
    description: 'Your AI agents pick up every call, day and night. Zero hold time, zero missed leads.',
  },
  {
    icon: Globe,
    title: '30+ Languages',
    description: 'Fluent in Dutch, English, German, Spanish and more. One agent, every market.',
  },
  {
    icon: Zap,
    title: 'Instant Resolution',
    description: 'Orders, returns, complaints — resolved in real-time with full system access.',
  },
  {
    icon: BarChart3,
    title: 'Scale Infinitely',
    description: '1 call or 10,000 simultaneously. No hiring, no training, no sick days.',
  },
];

const stats = [
  { value: '<2s', label: 'Avg. pickup time' },
  { value: '94%', label: 'First-call resolution' },
  { value: '30+', label: 'Languages supported' },
  { value: '24/7', label: 'Always available' },
];

export default function SplashPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden noise-overlay">
      {/* Animated background — uses theme glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute top-[-30%] left-[50%] -translate-x-1/2 w-[1000px] h-[1000px] rounded-full blur-[150px] animate-pulse"
          style={{ background: `hsl(${theme.colors.primary} / 0.07)`, animationDuration: '8s' }}
        />
        <div
          className="absolute top-[20%] right-[-10%] w-[600px] h-[600px] rounded-full blur-[120px] animate-pulse"
          style={{ background: `hsl(${theme.gradientEnd} / 0.05)`, animationDuration: '12s' }}
        />
        <div
          className="absolute bottom-[-20%] left-[-5%] w-[500px] h-[500px] rounded-full blur-[100px]"
          style={{ background: `hsl(${theme.colors.accent} / 0.04)` }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${theme.glowColor} 1px, transparent 1px), linear-gradient(90deg, ${theme.glowColor} 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Top nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-5 animate-enter">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shadow-lg"
            style={{ background: `linear-gradient(135deg, hsl(${theme.gradientStart}), hsl(${theme.gradientEnd}))`, boxShadow: `0 4px 14px ${theme.glowColor}` }}
          >
            <Headphones className="w-4 h-4 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
            {theme.name.toLowerCase()}
          </span>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Sign In
        </button>
      </nav>

      {/* Hero */}
      <div className="relative z-10 flex flex-col items-center pt-16 sm:pt-24 pb-12 px-4">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-8 backdrop-blur-sm animate-stagger-1"
            style={{
              background: `hsl(${theme.colors.primary} / 0.1)`,
              border: `1px solid hsl(${theme.colors.primary} / 0.2)`,
              color: `hsl(${theme.colors.primary})`,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: `hsl(${theme.colors.primary})` }} />
            Now in private beta
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-extrabold tracking-tight mb-6 leading-[0.95] animate-stagger-2">
            <span className="bg-gradient-to-b from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              {theme.heroTitle ? theme.heroTitle.split(' ').slice(0, 3).join(' ') : `Welcome to ${theme.name}`}
            </span>
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(to right, hsl(${theme.gradientStart}), hsl(${theme.colors.primary}), hsl(${theme.gradientEnd}))` }}
            >
              {theme.heroTitle ? theme.heroTitle.split(' ').slice(3).join(' ') || theme.name : 'Voice Demo'}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed animate-stagger-3">
            {theme.heroSubtitle || `AI voice agents that handle sales, support, and service calls —`}{' '}
            {!theme.heroSubtitle && <span className="text-foreground/80">in any language, at any scale, around the clock.</span>}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8 animate-stagger-4">
            <button
              onClick={() => navigate('/request-access')}
              className="btn-primary group w-full sm:w-auto px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl flex items-center justify-center gap-2"
              style={{ boxShadow: `0 8px 24px ${theme.glowColor}` }}
            >
              Request Early Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="btn-ghost w-full sm:w-auto px-8 py-4 text-muted-foreground hover:text-foreground font-semibold rounded-xl border border-border hover:border-foreground/20 transition-all"
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-20 w-full max-w-3xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 animate-stagger-5">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-b from-foreground to-muted-foreground bg-clip-text text-transparent mb-1">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features grid */}
        <div className="mt-20 w-full max-w-5xl mx-auto">
          <h2 className="text-center text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-8 animate-stagger-5">
            Why teams switch to {theme.name}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <div
                key={feature.title}
                className="group relative rounded-2xl p-6 card-lift cursor-default backdrop-blur-sm"
                style={{
                  background: `linear-gradient(135deg, hsl(${theme.colors.primary} / 0.08), hsl(${theme.gradientEnd} / 0.04))`,
                  border: `1px solid hsl(${theme.colors.primary} / 0.12)`,
                  animation: `enter 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.15 + idx * 0.07}s both`,
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="p-2.5 rounded-xl"
                    style={{
                      background: `hsl(${theme.colors.foreground} / 0.06)`,
                      border: `1px solid hsl(${theme.colors.foreground} / 0.06)`,
                    }}
                  >
                    <feature.icon className="w-6 h-6" style={{ color: `hsl(${theme.colors.primary})` }} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-24 text-center animate-stagger-6">
          <p className="text-muted-foreground text-sm mb-4">Ready to never miss a call again?</p>
          <button
            onClick={() => navigate('/request-access')}
            className="btn-ghost group px-8 py-3.5 text-foreground font-semibold rounded-xl border border-border hover:border-foreground/20 transition-all inline-flex items-center gap-2"
          >
            Get Started
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Footer */}
        <div className="mt-20 pb-8 text-center text-xs text-muted-foreground/50">
          {theme.name}
        </div>
      </div>
    </div>
  );
}
